// @ts-check

const path = require('path');
const {
  SUPPORTED_LOCALES_OBJ,
  SUPPORTED_LOCALES_ARR,
} = require('@zep-us/i18n');

// 이 config 는 server/client 양쪽 런타임에서 require 되며, isBrowser 는 각 런타임에서 평가된다.
const isBrowser = typeof window !== 'undefined';

// 세션(인스턴스)당 같은 키는 1회만 Sentry 로 보고 → 알림 폭주 방지.
const reportedMissingKeys = new Set();

/**
 * ZEP-443 안전망: dead key 빌드타임 제외(i18n-exclude-keys.json)로 잘못 빠진 키가
 * 실제 prod 화면에서 호출되면 Sentry 에 경고를 남긴다.
 * - 클라이언트 + PRD 한정 (서버 SSR / dev 는 무시)
 * - 같은 키는 세션당 1회, Sentry fingerprint 로 키별 1개 이슈로 묶음
 * - defaultValue / <Trans> children 등 의도된 폴백이 있으면 유저는 정상 텍스트를
 *   보므로 보고하지 않는다(노이즈 차단). fallbackValue 가 비었거나 key 와 같을 때만
 *   = 진짜 raw key 가 화면에 노출되는 경우 → 보고.
 * @type {NonNullable<import('next-i18next').UserConfig['missingKeyHandler']>}
 */
const missingKeyHandler = (
  lngs,
  ns,
  key,
  fallbackValue,
  _updateMissing,
  options,
) => {
  if (!isBrowser) return;
  if (process.env.NEXT_PUBLIC_STAGE !== 'PRD') return;
  // t(key, { defaultValue }) 또는 <Trans> children 처럼 의도된 폴백이 있으면 스킵.
  const hasIntendedFallback =
    (options && options.defaultValue != null) ||
    (typeof fallbackValue === 'string' &&
      fallbackValue !== '' &&
      fallbackValue !== key);
  if (hasIntendedFallback) return;
  const id = `${ns}:${key}`;
  if (reportedMissingKeys.has(id)) return;
  reportedMissingKeys.add(id);
  try {
    // Sentry 는 sentry.client.config 에서 이미 init 됨.
    require('@sentry/nextjs').captureMessage(`i18n missing key: ${id}`, {
      level: 'warning',
      tags: { i18n_ns: ns, i18n_key: key, locale: String(lngs && lngs[0]) },
      fingerprint: ['i18n-missing-key', ns, key],
    });
  } catch {
    // Sentry 미초기화 등은 조용히 무시 (안전망이 앱을 깨뜨리면 안 됨)
  }
};

/** @type {import('next-i18next').UserConfig} */
module.exports = {
  i18n: {
    defaultLocale: SUPPORTED_LOCALES_OBJ.ko,
    locales: [...SUPPORTED_LOCALES_ARR],
    localeDetection: false,
  },
  debug: false,
  supportedLngs: SUPPORTED_LOCALES_ARR,
  detection: {
    order: [
      'querystring',
      'cookie',
      'localStorage',
      'navigator',
      'htmlTag',
      'path',
      'subdomain',
    ],
  },
  interpolation: {
    escapeValue: false,
  },
  fallbackLng: false,
  localePath: path.resolve('./public/locales'),
  // missingKeyHandler(함수)가 userConfig 에 들어가면서 serverSideTranslations 의
  // 기본 직렬화(serializeConfig:true)가 "function cannot be serialized" 로 빌드를
  //깨뜨린다. config 는 _app 에서 appWithTranslation 으로 직접 주입하므로 직렬화 불필요.
  // @see https://github.com/i18next/next-i18next/blob/v15.2.0/README.md#unserializable-configs
  serializeConfig: false,
  // ZEP-443: 빠진 키 감지 안전망. 클라이언트에서만 켜서 서버 fs-backend 의
  // .missing.json 쓰기를 막고, 누락 키는 missingKeyHandler 로만 보고한다.
  saveMissing: isBrowser,
  missingKeyHandler,
};
